"use client";

import { lazy, Suspense, useCallback, useEffect, useState } from "react";

import { format, isAfter, startOfDay } from "date-fns";
import type { DateRange } from "react-day-picker";
import type { Control, FieldValues, Path, useForm } from "react-hook-form";

import { FlightSpinLoader } from "@repo/ui/components";
import { formatDateToDayMonth } from "@repo/ui/helpers/formatDateToDayMonth";
import { getDateFnsLocale } from "@repo/ui/utils/dateFnsLocale";

import { useAppSelector } from "@repo/store/hooks";
import { selectLocale } from "@repo/store/slices";

import { FormDescription, FormField, FormItem, FormLabel } from "../components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import type { FareCalendarMap } from "../constants/flightAvailability";
import { useMediaQuery } from "../Hooks/useMediaQuery";
import { cn } from "../lib/utils";
import type { CommonInfo } from "../types/cms";
import DateInputField from "./DatePicker/DateInputField";

// Loaded lazily so the heavy react-day-picker calendar is fetched only when a
// date popover is opened, keeping it out of the initial search-widget bundle.
const RangeCalendarPanel = lazy(() => import("./DatePicker/RangeCalendarPanel"));

const CalendarFallback = () => (
  <div className="flex items-center justify-center p-8">
    <FlightSpinLoader />
  </div>
);

type RangeDate = DateRange | undefined;

export type CalendarState = {
  outbound: FareCalendarMap;
  inbound?: FareCalendarMap;
};

interface DatePickerFieldProps<T extends FieldValues> {
  control: Control<T>;
  form: ReturnType<typeof useForm<T>>;
  name: Path<T>;
  placeholder?: string;
  isRequired: boolean;
  className?: string;
  description?: string;
  disabled?: boolean;
  isRoundTrip?: boolean;
  dateRange?: RangeDate;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  bgColor?: string;
  calendarFares: CalendarState;
  handleDateSelect: (start: Date | undefined, end?: Date | undefined) => void; // called when user finished selecting (we call it on inbound selection)
  onMonthChange: (monthDate: Date) => void;
  showFare?: boolean;
  size?: "default" | "compact";
  shortenDate?: boolean;
  loadingFares?: boolean;
  returnDateLabel?: string;
  noOfMonthsToShowFareCache: number;
  testId?: string;
  commonInfo?: CommonInfo;
  dateFormat?: string;
  align?: "start" | "center" | "end";
}

export default function DatePickerField<T extends FieldValues>({
  control,
  form,
  name,
  placeholder = "Select date",
  dateRange,
  isRequired = false,
  className,
  description,
  disabled = false,
  isRoundTrip,
  isOpen,
  setIsOpen,
  calendarFares,
  handleDateSelect,
  onMonthChange,
  bgColor,
  size,
  showFare,
  shortenDate,
  loadingFares,
  returnDateLabel = "Return date",
  noOfMonthsToShowFareCache,
  testId,
  commonInfo,
  dateFormat = "PPP",
  align = "start",
}: DatePickerFieldProps<T>) {
  const isDesktop = useMediaQuery("(min-width: 960px)");
  const locale = useAppSelector(selectLocale);
  const dateFnsLocale = getDateFnsLocale(locale);
  // open state for single-mode (keeps older behavior)
  const [openState, setOpenState] = useState(false);

  // separate popovers for roundtrip
  const [openOutbound, setOpenOutbound] = useState(false);
  const [openInbound, setOpenInbound] = useState(false);

  const open = typeof isOpen === "boolean" ? isOpen : openState;
  const handleOpenChange = useCallback(
    (val: boolean) => {
      if (!disabled) {
        setIsOpen?.(val);
        setOpenState(val);
      }
    },
    [disabled, setIsOpen],
  );

  useEffect(() => {
    if (isOpen && !dateRange?.from) {
      setOpenOutbound(true);
    }
  }, [isOpen, dateRange?.from]);

  const formatLabel = (selected: Date | undefined, fallbackPlaceholder?: string) => {
    if (!selected) return fallbackPlaceholder || placeholder;
    if (shortenDate) {
      if (commonInfo) {
        const day = selected.getDate();
        const weekdayNames = [
          commonInfo.sundayShort,
          commonInfo.mondayShort,
          commonInfo.tuesdayShort,
          commonInfo.wednesdayShort,
          commonInfo.thursdayShort,
          commonInfo.fridayShort,
          commonInfo.saturdayShort,
        ];
        const months = [
          commonInfo.januaryShort,
          commonInfo.februaryShort,
          commonInfo.marchShort,
          commonInfo.aprilShort,
          commonInfo.mayShort,
          commonInfo.juneShort,
          commonInfo.julyShort,
          commonInfo.augustShort,
          commonInfo.septemberShort,
          commonInfo.octoberShort,
          commonInfo.novemberShort,
          commonInfo.decemberShort,
        ];
        const rawDayName = weekdayNames[selected.getDay()] || "";
        const isEnglish =
          locale?.toLowerCase().startsWith("en") ||
          commonInfo?.januaryLabel?.toLowerCase().startsWith("janu") ||
          false;

        let dayName = "";
        if (isEnglish) {
          // Remove any dots/commas if they are already in the string, then add comma
          const cleanDay = rawDayName.replace(/[.,]/g, "");
          dayName = `${cleanDay},`;
        } else {
          dayName = rawDayName.endsWith(".") ? rawDayName : `${rawDayName}.`;
        }

        const rawMonthName = months[selected.getMonth()] || "";
        // English: capitalize first letter (e.g. "sep" -> "Sep")
        // Spanish/other: lowercase (e.g. "Sept" -> "sept")
        const monthName = isEnglish
          ? rawMonthName.charAt(0).toUpperCase() + rawMonthName.slice(1).toLowerCase()
          : rawMonthName.toLowerCase();

        return `${dayName} ${day} ${monthName}`;
      }
      // Example: Wed 17-09
      return formatDateToDayMonth(selected);
    }

    return format(selected, dateFormat, { locale: dateFnsLocale });
  };

  const formatters = commonInfo
    ? {
        // weekday name used for aria-label; WeekHeader will substring(0,3)
        formatWeekdayName: (d: Date) => {
          const map = [
            commonInfo.sundayShort,
            commonInfo.mondayShort,
            commonInfo.tuesdayShort,
            commonInfo.wednesdayShort,
            commonInfo.thursdayShort,
            commonInfo.fridayShort,
            commonInfo.saturdayShort,
          ];
          return map[d.getDay()] || "";
        },
        // month caption
        formatCaption: (m: Date) => {
          const months = [
            commonInfo.januaryLabel,
            commonInfo.februaryLabel,
            commonInfo.marchLabel,
            commonInfo.aprilLabel,
            commonInfo.mayLabel,
            commonInfo.juneLabel,
            commonInfo.julyLabel,
            commonInfo.augustLabel,
            commonInfo.septemberLabel,
            commonInfo.octoberLabel,
            commonInfo.novemberLabel,
            commonInfo.decemberLabel,
          ];
          const label = months[m.getMonth()] || "";
          return `${label} ${m.getFullYear()}`;
        },
      }
    : undefined;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const currentRange: RangeDate = (dateRange ?? field.value) as RangeDate;

        const fromError = form.getFieldState("travelDates.from" as Path<T>).error;
        const toError = form.getFieldState("travelDates.to" as Path<T>).error;

        const resolveInbound = (outbound: Date | undefined, inbound: Date | undefined) =>
          outbound && inbound && isAfter(startOfDay(outbound), startOfDay(inbound))
            ? undefined
            : inbound;

        // helpers to update the shared range value
        const setOutbound = (d: Date | undefined, to: Date | undefined) => {
          const next: DateRange | undefined = d ? { from: d, to } : undefined;
          field.onChange(next);
        };
        const setInbound = (d: Date | undefined) => {
          const next: DateRange | undefined = d ? { from: currentRange?.from, to: d } : undefined;
          field.onChange(next);
        };

        // ensure only one roundtrip popover is open at a time
        const openOutboundOnly = (v: boolean) => {
          setOpenOutbound(v);
          if (v) setOpenInbound(false);
          setIsOpen?.(v);
        };
        const openInboundOnly = (v: boolean) => {
          setOpenInbound(v);
          if (v) setOpenOutbound(false);
          setIsOpen?.(v);
        };

        return (
          <FormItem
            className={cn(disabled && "cursor-not-allowed opacity-50", className)}
            data-testid={testId}
          >
            <FormLabel hidden>{placeholder}</FormLabel>

            {/* ---------- SINGLE / ONEWAY (original behavior) ---------- */}
            {!isRoundTrip && (
              <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild disabled={disabled}>
                  <div>
                    <DateInputField
                      label={formatLabel(currentRange?.from, placeholder)}
                      labelColor={
                        currentRange?.from
                          ? "text-brand-blue"
                          : "text-muted-foreground group-hover:text-brand-gray"
                      }
                      placeholder={placeholder}
                      disabled={disabled}
                      invalid={!!fromError}
                      name="travelDates.from"
                      className={`${bgColor ? bgColor : ""} border-brand-black-60 h-full`}
                    />
                    {fromError?.message && (
                      <p className="mt-2 text-sm text-destructive">{fromError.message}</p>
                    )}
                  </div>
                </PopoverTrigger>

                <PopoverContent
                  align={isDesktop ? align : "center"}
                  className={`w-[95vw] max-w-[398px] md:max-w-none md:w-auto p-0 max-h-[85vh] overflow-y-auto ${size === "compact" ? "md:mt-2" : ""}`}
                >
                  {loadingFares && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                      <FlightSpinLoader />
                    </div>
                  )}
                  <Suspense fallback={<CalendarFallback />}>
                    <RangeCalendarPanel
                      isDesktop={isDesktop}
                      locale={dateFnsLocale}
                      selected={currentRange?.from}
                      onSelect={(date) => {
                        setOutbound(date, resolveInbound(date, currentRange?.to));
                        handleDateSelect(date); // only one-way, fine
                        setOpenState(false);
                      }}
                      onMonthChange={(monthDate) => onMonthChange?.(monthDate)}
                      required={isRequired}
                      dayFareMap={calendarFares.outbound}
                      isRoundtrip={false}
                      showFare={showFare}
                      noOfMonthsToShowFareCache={noOfMonthsToShowFareCache}
                      size={size}
                      testIdSuffix="outbound"
                      formatters={formatters}
                    />
                  </Suspense>
                </PopoverContent>
              </Popover>
            )}

            {/* ---------- ROUNDTRIP: two independent popovers ---------- */}
            {isRoundTrip && (
              <div className="grid grid-cols-2 gap-2 md:gap-4">
                {/* Outbound popover */}
                <Popover open={openOutbound} onOpenChange={openOutboundOnly}>
                  <PopoverTrigger asChild disabled={disabled}>
                    <div>
                      <DateInputField
                        label={formatLabel(currentRange?.from, placeholder)}
                        labelColor={
                          currentRange?.from
                            ? "text-brand-blue"
                            : "text-muted-foreground group-hover:text-brand-gray"
                        }
                        placeholder={placeholder}
                        disabled={disabled}
                        invalid={!!fromError}
                        name="travelDates.from"
                        className={`${bgColor ? bgColor : ""} border-brand-black-60 h-full`}
                      />
                      {fromError?.message && (
                        <p className="mt-2 text-sm text-destructive">{fromError.message}</p>
                      )}
                    </div>
                  </PopoverTrigger>

                  <PopoverContent
                    align={align}
                    alignOffset={0}
                    className={`w-[95vw] mr-2 max-w-[398px] md:max-w-none md:w-auto p-0 max-h-[85vh] overflow-y-auto ${size === "compact" ? "md:mt-2" : ""}`}
                  >
                    {loadingFares && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                        <FlightSpinLoader />
                      </div>
                    )}
                    <Suspense fallback={<CalendarFallback />}>
                      <RangeCalendarPanel
                        isDesktop={isDesktop}
                        locale={dateFnsLocale}
                        selected={currentRange?.from}
                        onSelect={(date) => {
                          const nextInbound = resolveInbound(date, currentRange?.to);
                          setOutbound(date, nextInbound);
                          handleDateSelect(date, nextInbound);
                          setOpenOutbound(false);
                          if (date) {
                            setOpenInbound(true);
                          }
                        }}
                        onMonthChange={(monthDate) => onMonthChange?.(monthDate)}
                        required={isRequired}
                        dayFareMap={
                          openInbound ? (calendarFares.inbound ?? {}) : calendarFares.outbound
                        }
                        outboundSelected={currentRange?.from}
                        isRoundtrip
                        showFare={showFare}
                        noOfMonthsToShowFareCache={noOfMonthsToShowFareCache}
                        size={size}
                        testIdSuffix="outbound"
                        formatters={formatters}
                      />
                    </Suspense>
                  </PopoverContent>
                </Popover>

                {/* Inbound popover */}
                <Popover open={openInbound} onOpenChange={openInboundOnly}>
                  <PopoverTrigger asChild disabled={disabled}>
                    <div>
                      <DateInputField
                        label={formatLabel(currentRange?.to, returnDateLabel)}
                        labelColor={
                          currentRange?.to
                            ? "text-brand-blue"
                            : "text-muted-foreground group-hover:text-brand-gray"
                        }
                        placeholder={returnDateLabel}
                        disabled={disabled}
                        invalid={!!toError}
                        name="travelDates.to"
                        className={`${bgColor ? bgColor : ""} border-brand-black-60 h-full`}
                      />
                      {toError?.message && (
                        <p className="text-sm font-medium text-destructive">{toError.message}</p>
                      )}
                    </div>
                  </PopoverTrigger>

                  <PopoverContent
                    align={isDesktop ? align : "end"}
                    className={`w-[95vw] ml-2 max-w-[398px] md:max-w-none md:w-auto p-0 max-h-[85vh] overflow-y-auto ${size === "compact" ? "md:mt-2" : ""}`}
                  >
                    {loadingFares && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                        <FlightSpinLoader />
                      </div>
                    )}

                    <Suspense fallback={<CalendarFallback />}>
                      <RangeCalendarPanel
                        isDesktop={isDesktop}
                        locale={dateFnsLocale}
                        selected={currentRange?.to}
                        onSelect={(date) => {
                          setInbound(date);
                          handleDateSelect(currentRange?.from, date);
                          setOpenInbound(false);
                        }}
                        onMonthChange={(monthDate) => onMonthChange?.(monthDate)}
                        required={isRequired}
                        beforeDate={currentRange?.from}
                        dayFareMap={
                          openInbound ? (calendarFares.inbound ?? {}) : calendarFares.outbound
                        }
                        outboundSelected={currentRange?.from}
                        inboundSelected={currentRange?.to}
                        isRoundtrip
                        showFare={showFare}
                        noOfMonthsToShowFareCache={noOfMonthsToShowFareCache}
                        size={size}
                        testIdSuffix="inbound"
                        formatters={formatters}
                      />
                    </Suspense>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {description && <FormDescription>{description}</FormDescription>}
          </FormItem>
        );
      }}
    />
  );
}
